import { MimeTypeExtended } from '@sosocio/creator-types/app';

export const FILE_EXTENSIONS_DESIGN_PRINT_BROWSER_RASTER: string[] = [
	'avif',
	'gif',
	'jpeg',
	'jpg',
	'png',
	'webp',
];

export const MIME_TYPES_JPEG: MimeTypeExtended[] = ['image/jpeg'];
export const MIME_TYPES_PNG: MimeTypeExtended[] = ['image/png'];
export const MIME_TYPES_GIF: MimeTypeExtended[] = ['image/gif'];
export const MIME_TYPES_WEBP: MimeTypeExtended[] = ['image/webp'];
export const MIME_TYPES_AVIF: MimeTypeExtended[] = ['image/avif'];
export const MIME_TYPES_HEIC: MimeTypeExtended[] = [
	'image/heic',
	'image/heif',
];
export const MIME_TYPES_SVG: MimeTypeExtended[] = ['image/svg+xml'];
export const MIME_TYPES_PDF: MimeTypeExtended[] = ['application/pdf'];
export const MIME_TYPES_ILLUSTRATOR: MimeTypeExtended[] = ['application/postscript'];
export const MIME_TYPES_EPS: MimeTypeExtended[] = [
	'application/postscript',
	'application/eps',
];
export const MIME_TYPES_CORELDRAW: MimeTypeExtended[] = ['application/vnd.cdr'];

// XML MIME types that process-photo treats as SVG when the markup is an SVG document
export const MIME_TYPES_XML: string[] = [
	'application/xml',
	'text/xml',
];

export const MIME_TYPES_BROWSER_SUPPORTED: MimeTypeExtended[] = [
	...MIME_TYPES_JPEG,
	...MIME_TYPES_PNG,
	...MIME_TYPES_GIF,
	...MIME_TYPES_WEBP,
	...MIME_TYPES_AVIF,
	...MIME_TYPES_SVG,
];

export const MIME_TYPES_RASTER_IMAGES: MimeTypeExtended[] = [
	...MIME_TYPES_JPEG,
	...MIME_TYPES_PNG,
	...MIME_TYPES_GIF,
	...MIME_TYPES_WEBP,
	...MIME_TYPES_AVIF,
];

export const MIME_TYPES_WITH_EMBEDDED_IMAGES: MimeTypeExtended[] = [
	...MIME_TYPES_SVG,
	...MIME_TYPES_PDF,
	...MIME_TYPES_EPS,
	...MIME_TYPES_ILLUSTRATOR,
	...MIME_TYPES_CORELDRAW,
];

export const MIME_TYPES_REQUIRE_CONVERSION: MimeTypeExtended[] = [
	...MIME_TYPES_HEIC,
	...MIME_TYPES_PDF,
	...MIME_TYPES_EPS,
	...MIME_TYPES_ILLUSTRATOR,
	...MIME_TYPES_CORELDRAW,
];

export const MIME_TYPES_SHOWN_AS_PNG: MimeTypeExtended[] = [
	...MIME_TYPES_PNG,
	...MIME_TYPES_GIF,
	...MIME_TYPES_PDF,
	...MIME_TYPES_ILLUSTRATOR,
	...MIME_TYPES_EPS,
];

export const FILE_EXTENSIONS_SHOWN_AS_SVG: string[] = [
	'cdr',
	'ai',
	'eps',
	'pdf',
];

/**
 * Resolve the upload types enabled for a brand, matching the formats process-photo supports.
 *
 * @param enabled Per-brand upload switches from the config store.
 * @returns MIME types for validation and the file input `accept` filter.
 */
export function getAcceptedUploadFileTypes(enabled: {
	eps: boolean;
	heic: boolean;
	pdf: boolean;
	svg: boolean;
}): {
	accept: string;
	mimeTypes: MimeTypeExtended[];
} {
	const mimeTypes = [...MIME_TYPES_BROWSER_SUPPORTED];
	// SVG saved as XML is accepted after header inspection, so the picker must allow XML files
	const accept: string[] = [...MIME_TYPES_XML];

	if (enabled.heic) {
		mimeTypes.push(...MIME_TYPES_HEIC);
	}
	if (enabled.pdf) {
		mimeTypes.push(...MIME_TYPES_PDF);
	}
	if (enabled.svg) {
		mimeTypes.push(...MIME_TYPES_SVG);
	}
	if (enabled.eps) {
		// CorelDRAW is a vector format like EPS and AI, so it shares their switch
		mimeTypes.push(
			...MIME_TYPES_EPS,
			...MIME_TYPES_CORELDRAW,
		);
		// Browsers report no MIME type for .cdr files, so the picker needs the extension
		accept.push('.cdr');
	}

	return {
		accept: [
			...mimeTypes,
			...accept,
		].join(),
		mimeTypes,
	};
}

// Extensions shown in the upload hint, in display order: common rasters first, then vectors
const UPLOAD_FORMAT_EXTENSIONS: {
	extension: string;
	mimeType: MimeTypeExtended;
}[] = [
	{
		extension: '.JPG',
		mimeType: 'image/jpeg',
	},
	{
		extension: '.JPEG',
		mimeType: 'image/jpeg',
	},
	{
		extension: '.PNG',
		mimeType: 'image/png',
	},
	{
		extension: '.GIF',
		mimeType: 'image/gif',
	},
	{
		extension: '.WEBP',
		mimeType: 'image/webp',
	},
	{
		extension: '.AVIF',
		mimeType: 'image/avif',
	},
	{
		extension: '.HEIC',
		mimeType: 'image/heic',
	},
	{
		extension: '.HEIF',
		mimeType: 'image/heif',
	},
	{
		extension: '.SVG',
		mimeType: 'image/svg+xml',
	},
	{
		extension: '.PDF',
		mimeType: 'application/pdf',
	},
	{
		extension: '.EPS',
		mimeType: 'application/eps',
	},
	{
		extension: '.AI',
		mimeType: 'application/postscript',
	},
	{
		extension: '.CDR',
		mimeType: 'application/vnd.cdr',
	},
];

/**
 * List the file extensions to show in the upload hint for the accepted MIME types.
 *
 * @param mimeTypes Accepted MIME types from `getAcceptedUploadFileTypes`.
 * @returns Display extensions in a fixed order, e.g. `.JPG`.
 */
export function getUploadFormatExtensions(mimeTypes: MimeTypeExtended[]): string[] {
	return UPLOAD_FORMAT_EXTENSIONS
		.filter(({ mimeType }) => mimeTypes.includes(mimeType))
		.map(({ extension }) => extension);
}
